import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import LaneNav from "../components/LaneNav";
import { Plus, ArrowRight, ArrowDown, Clock, ArrowSquareOut, Gift, Warning } from "@phosphor-icons/react";
import { outboundRetailerUrl, trackBuyNowClick } from "../lib/retailerLinks";
import { claimWindowStatus } from "../lib/retailerPolicies";
import { SkeletonGrid } from "../components/Skeleton";
import FeedbackCTA from "../components/FeedbackCTA";

const PLACEHOLDERS = [
  "https://images.unsplash.com/photo-1712878696627-fd59c375dbb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzbWFydCUyMGhvbWUlMjBsaXZpbmclMjByb29tJTIwZWxlY3Ryb25pY3N8ZW58MHx8fHwxNzc5NTMxMzgyfDA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/28087045/pexels-photo-28087045.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

/**
 * /dashboard/purchases — dedicated standalone page for tracked
 * purchases (parallel to /dashboard/wishlist and /dashboard/vault).
 * Restored on user request after we'd briefly inlined the list onto
 * the main Dashboard. Now the Dashboard surfaces the Service Hub +
 * household summary + sales-ending-soon, and the full list lives
 * here so it scales without crowding the hub.
 */
export default function Purchases() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/purchases");
        if (!cancelled) setItems(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <LaneNav active="purchases" />

        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="pg-overline mb-2">Lane 2 of 3 · After you buy</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Tracked purchases<span className="text-[#002FA7]">.</span></h1>
            <p className="text-sm text-[#52525b] mt-3 max-w-2xl leading-relaxed">
              Items you've already bought. We watch the live price every day for six weeks after purchase so we catch second-wave drops the retailer may make weeks after the initial sale, and draft a polite refund claim if the retailer's price-adjustment window is still open.
            </p>
          </div>
          <Link to="/dashboard/add" className="bg-[#002FA7] text-white font-bold px-5 py-3 inline-flex items-center justify-center gap-2 hover:bg-[#00207A] transition-colors whitespace-nowrap" data-testid="purchases-add-btn">
            <Plus size={16} weight="bold" /> Add a purchase
          </Link>
        </div>

        {loading ? (
          <SkeletonGrid count={6} />
        ) : items.length === 0 ? (
          <PurchasesEmpty />
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <span className="pg-overline" data-testid="items-count">{items.length} item{items.length === 1 ? "" : "s"} being watched</span>
              <span className="text-[10px] font-mono-pg text-[#52525b] uppercase tracking-widest">Watching daily · 6 weeks per item</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((p, idx) => <PurchaseCard key={p.purchase_id} p={p} idx={idx} />)}
            </div>
          </>
        )}

        <div className="mt-12">
          <FeedbackCTA variant="card" />
        </div>
      </main>
    </div>
  );
}

function PurchaseCard({ p, idx }) {
  const diff = p.purchase_price - p.current_price;
  const isDrop = diff > 0;
  const pct = ((diff / p.purchase_price) * 100).toFixed(1);
  const expDate = new Date(p.tracking_expires_at);
  const daysLeft = Math.max(0, Math.ceil((expDate - new Date()) / (1000 * 60 * 60 * 24)));
  const claim = claimWindowStatus(p.retailer_id, p.purchase_date);
  const isStale = (() => {
    if (!p.last_scrape_at) return false;
    if (p.last_scrape_status === "error") return true;
    try {
      const ms = Date.now() - new Date(p.last_scrape_at).getTime();
      return ms > 48 * 60 * 60 * 1000;
    } catch { return false; }
  })();
  return (
    <div className="pg-card pg-fade relative flex flex-col" style={{ animationDelay: `${idx * 40}ms` }} data-testid={`purchase-card-${p.purchase_id}`}>
      {p.is_demo && (
        <span className="absolute top-3 right-3 text-[9px] font-bold tracking-widest bg-black text-white px-1.5 py-0.5 z-10" data-testid={`demo-badge-${p.purchase_id}`}>DEMO</span>
      )}
      {isStale && !p.is_demo && (
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] font-bold tracking-widest bg-[#D97706] text-white px-1.5 py-0.5 z-10"
          title={p.last_scrape_status === "error" ? "Last price refresh failed - we'll keep trying in the background" : "Price hasn't refreshed in over 48 hours"}
          data-testid={`stale-badge-${p.purchase_id}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span> STALE
        </span>
      )}
      <Link to={`/dashboard/purchase/${p.purchase_id}`} className="p-5 block flex-1" data-testid={`purchase-card-body-${p.purchase_id}`}>
        <div className="flex gap-4">
          <div className="w-16 h-16 bg-[#f0f0f2] flex-shrink-0 overflow-hidden">
            <img src={p.image_url || PLACEHOLDERS[idx % PLACEHOLDERS.length]} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="pg-overline">{p.retailer_name}</div>
            <h3 className="font-heading font-bold tracking-tight truncate mt-1">{p.product_name}</h3>
            <div className="font-mono-pg text-xs text-[#52525b] mt-1">{p.category}</div>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 border-t border-[#e4e4e7] pt-4">
          <div>
            <div className="pg-overline">Paid</div>
            <div className="font-mono-pg font-semibold mt-1">${p.purchase_price.toFixed(2)}</div>
          </div>
          <div>
            <div className="pg-overline">Current</div>
            <div className={`font-mono-pg font-semibold mt-1 ${isDrop ? "text-[#D97706]" : ""}`}>${p.current_price.toFixed(2)}</div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          {isDrop ? (
            claim.windowOpen ? (
              <span className="inline-flex items-center gap-1 bg-[#16A34A] text-white text-xs font-bold px-2 py-1" data-testid={`drop-badge-${p.purchase_id}`}>
                <ArrowDown size={12} weight="bold" /> {pct}% · claim ${diff.toFixed(0)}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-[#D97706] text-white text-xs font-bold px-2 py-1" data-testid={`drop-badge-${p.purchase_id}`} title="Price dropped but the retailer's claim window has closed - we can still draft a polite goodwill ask">
                <ArrowDown size={12} weight="bold" /> {pct}% · goodwill ask
              </span>
            )
          ) : (
            <span className="inline-flex items-center gap-1 border border-[#e4e4e7] text-xs font-semibold px-2 py-1 text-[#52525b]">
              No drop yet
            </span>
          )}
          <div className="flex flex-col items-end gap-0.5 text-right">
            {claim.policyDays && (
              <span
                className={`text-[10px] font-mono-pg uppercase tracking-widest ${claim.windowOpen ? "text-[#16A34A]" : "text-[#52525b]"}`}
                title={`${p.retailer_name}'s published claim window is ${claim.policyDays} days from purchase. ${claim.source}.`}
                data-testid={`claim-window-${p.purchase_id}`}
              >
                {claim.windowOpen ? `Claim: ${claim.daysLeftInWindow}d left` : "Claim window closed"}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-[10px] font-mono-pg uppercase tracking-widest text-[#52525b]" title="Days remaining in the 6-week tracking window">
              <Clock size={10} /> Watching {daysLeft}d
            </span>
          </div>
        </div>
      </Link>
      {p.product_url && (
        <a
          href={outboundRetailerUrl(p.product_url, p.retailer_id)}
          target="_blank"
          rel="sponsored nofollow noopener noreferrer"
          onClick={() => trackBuyNowClick({ retailerId: p.retailer_id, productUrl: p.product_url, context: "purchases_card" })}
          className="border-t border-[#e4e4e7] px-5 py-3 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-widest hover:bg-[#0a0a0a] hover:text-white transition-colors"
          data-testid={`card-buy-now-${p.purchase_id}`}
        >
          View live at {p.retailer_name} <ArrowSquareOut size={12} weight="bold" />
        </a>
      )}
      {p.promo && p.promo.code && p.promo.promo_price && (
        <div className="border-t border-[#16A34A]/40 bg-[#16A34A]/5 px-5 py-3" data-testid={`card-promo-${p.purchase_id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#16A34A]">
                Promo code available · save ${Number(p.promo.savings || 0).toFixed(0)}
              </div>
              <div className="font-mono-pg text-sm font-bold text-[#0a0a0a] mt-0.5 truncate">
                Code: <span className="bg-[#0a0a0a] text-white px-2 py-0.5 ml-1">{p.promo.code}</span>
              </div>
              <div className="text-[10px] text-[#52525b] mt-1">
                Drops the price to ${Number(p.promo.promo_price).toFixed(2)} at checkout
              </div>
            </div>
          </div>
        </div>
      )}
      {p.bonus && p.bonus.description && (
        <div className="border-t border-[#D97706]/40 bg-[#FFFBEB] px-5 py-3" data-testid={`card-bonus-${p.purchase_id}`}>
          <div className="flex items-start gap-2.5">
            <Gift size={16} weight="bold" className="text-[#D97706] flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#D97706]">
                Bonus included{p.bonus.value ? ` · worth $${Number(p.bonus.value).toFixed(0)}` : ""}
              </div>
              <div className="text-xs text-[#0a0a0a] mt-0.5 line-clamp-2">
                {p.bonus.description}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PurchasesEmpty() {
  return (
    <div className="border border-dashed border-[#a1a1aa] bg-[#f7f7f8] p-10 text-center" data-testid="purchases-empty">
      <h3 className="font-heading text-2xl font-bold tracking-tight">Nothing tracked yet.</h3>
      <p className="text-[#52525b] mt-2 max-w-md mx-auto">Add a recent purchase. We'll watch the live price daily for six weeks and tell you if the retailer drops it within their price-adjustment window so you can claim a refund.</p>
      <Link to="/dashboard/add" className="mt-6 inline-flex items-center gap-2 bg-[#002FA7] text-white font-bold px-6 py-3 hover:bg-[#00207A] transition-colors" data-testid="purchases-empty-add">
        <Plus size={16} weight="bold" /> Add your first purchase <ArrowRight size={16} weight="bold" />
      </Link>
    </div>
  );
}
